import { client } from './client';

import type { Game, GameRef, CreateGame, UpdateGame, MinimalGame, SearchableGame } from '../../models/Game';
import type { GameReservation } from '../../models/GameReservation';
import { MinimalUser, User } from '../../models/User';
import { uploadContentPicture } from './imageService';
import { Event } from '../../models/Event';
import { System } from '../../models/System';
import { Location } from '../../models/Location';
import { Tag } from '../../models/Tag';
import { isBefore } from 'date-fns';
import { Json } from './databaseTypes';
import { slugify } from '../../utils/slugify';
import { toMinimalUser } from './profileMappers';
import { throwIfSupabaseError } from './throwIfSupabaseError';

const table = 'games';

const gameSelectExpression = `
    id,
    legacy_id,
    events(
        id,
        name,
        description,
        opens_at,
        dates,
        max_games
    ),
    systems(
        id,
        name,
        description
    ),
    game_master:user_profiles!games_game_master_id_fkey(
        user_id,
        username,
        image_url,
        discord_username,
        role,
        gm_bans:gm_bans_players!gm_bans_players_gm_id_fkey(
            player:user_profiles!gm_bans_players_player_id_fkey(
                user_id,
                username,
                image_url,
                discord_username,
                role
            )
        )
    ),
    max_players,
    title,
    starts_at,
    image_url,
    description,
    short_description,
    locations(
        id,
        name,
        address
    ),
    notice,
    approved_at,
    is_draft,
    cloned_from_id,
    tags(
        id,
        name
    ),
    co_gms:game_co_gms(
        gm:user_profiles!game_co_gms_gm_user_id_fkey(
            user_id,
            username,
            image_url,
            discord_username,
            role
        )
    )
` as const;

// Lean projection for card/list views. Skips the heavy text columns
// (description, notice), location, system description, and the full
// players join — those are detail-page concerns. The `players(count)`
// aggregate gives a player-count without inflating N user_profile rows
// per game.
const gameListSelectExpression = `
    id,
    legacy_id,
    events(
        id,
        name,
        opens_at
    ),
    systems(
        id,
        name
    ),
    game_master:game_master_id(
        user_id,
        username,
        image_url,
        discord_username,
        role
    ),
    max_players,
    title,
    starts_at,
    image_url,
    short_description,
    approved_at,
    is_draft,
    tags(
        id,
        name
    ),
    players:games_players(count)
` as const;

export async function getAllGames(): Promise<Game[]> {
    const response = await client
        .from(table)
        .select(gameListSelectExpression)
        .neq('is_draft', true);
    throwIfSupabaseError(response);

    return response.data.map(parseListGame).sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime());
}

export async function getDraftGames(userId: User['id']): Promise<Game[]> {
    const response = await client
        .from(table)
        .select(gameListSelectExpression)
        .eq('game_master_id', userId)
        .is('approved_at', null)
        .eq('is_draft', true);
    throwIfSupabaseError(response);

    return response.data.map(parseListGame).sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime());
}

export async function getUnapprovedGames(): Promise<Game[]> {
    const response = await client
        .from(table)
        .select(gameListSelectExpression)
        .is('approved_at', null)
        .neq('is_draft', true);
    throwIfSupabaseError(response);

    return response.data.map(parseListGame).sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime());
}

// Batched variant of `getGamesForEvent` for the events archive / reservations
// / calendar flows that load games for many events at once. Returns all
// non-draft games whose `event_id` is in the provided list; callers group by
// `game.event.id` to bucket them per event.
export async function getGamesForEvents(eventIds: Event['id'][]): Promise<Game[]> {
    if (eventIds.length === 0) {
        return [];
    }

    const response = await client
        .from(table)
        .select(gameListSelectExpression)
        .in('event_id', eventIds)
        .neq('is_draft', true);
    throwIfSupabaseError(response);

    return response.data.map(parseListGame);
}

export async function getGamesForEvent(eventId: Event['id']): Promise<Game[]> {
    const response = await client
        .from(table)
        .select(gameListSelectExpression)
        .eq('event_id', eventId)
        .neq('is_draft', true);
    throwIfSupabaseError(response);

    return response.data.map(parseListGame).sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime());
}

export async function getGamesByTag(tagId: Tag['id']): Promise<Game[]> {
    const response = await client
        .from('games_tags')
        .select(`game:games(${gameListSelectExpression})`)
        .eq('tag_id', tagId)
        .neq('game.is_draft', true);
    throwIfSupabaseError(response);

    return response.data.filter((dto) => !!dto.game).map((outerDto) => {
        const dto = outerDto.game;
        return parseListGame(dto);
    }).sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime());
}

export async function getGamesBySystem(systemId: System['id']): Promise<Game[]> {
    const response = await client
        .from(table)
        .select(gameListSelectExpression)
        .eq('system_id', systemId)
        .neq('is_draft', true);
    throwIfSupabaseError(response);

    return response.data.map(parseListGame).sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime());
}

export async function getGamesInDateRange(fromDate: Date, toDate: Date): Promise<Game[]> {
    const response = await client
        .from(table)
        .select(gameListSelectExpression)
        .gte('starts_at', fromDate.toISOString())
        .lte('starts_at', toDate.toISOString())
        .neq('is_draft', true);
    throwIfSupabaseError(response);

    return response.data.map(parseListGame).sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime());
}

export async function findGameById(id: Game['id']): Promise<Game> {
    const response = await client
        .from(table)
        .select(gameSelectExpression)
        .eq('id', id)
        .limit(1)
        .single();
    throwIfSupabaseError(response);

    const baseGame = parseGame(response.data);
    const players = await getPlayersForGame(id);

    return {
        ...baseGame,
        players,
    };
}

async function getBannedPlayersForGm(gmUserId: User['id']): Promise<MinimalUser[]> {
    const response = await client
        .from('gm_bans_players')
        .select(`
            gm:user_profiles!gm_bans_players_gm_id_fkey!inner(user_id),
            player:user_profiles!gm_bans_players_player_id_fkey!inner(user_id, username, image_url, discord_username, role)
        `)
        .eq('gm.user_id', gmUserId);
    throwIfSupabaseError(response);

    return response.data.flatMap((dto) => dto.player == null ? [] : [toMinimalUser(dto.player)]);
}

export async function getGmBanUserIds(gmUserId: User['id']): Promise<User['id'][]> {
    const players = await getBannedPlayersForGm(gmUserId);
    return players.map((player) => player.id);
}

export async function updateGmBans(gmUserId: User['id'], playerUserIds: User['id'][]): Promise<void> {
    const currentBansResponse = await client
        .from('gm_bans_players')
        .select(`
            id,
            gm:user_profiles!gm_bans_players_gm_id_fkey!inner(id, user_id),
            player:user_profiles!gm_bans_players_player_id_fkey!inner(id, user_id)
        `)
        .eq('gm.user_id', gmUserId);
    throwIfSupabaseError(currentBansResponse);
    const currentBans = currentBansResponse.data;

    const desiredPlayerUserIds = new Set(playerUserIds);
    const currentPlayerUserIds = new Set(
        currentBans.flatMap((ban) => ban.player == null ? [] : [ban.player.user_id]),
    );

    const banIdsToRemove = currentBans
        .filter((ban) => ban.player != null && !desiredPlayerUserIds.has(ban.player.user_id))
        .map((ban) => ban.id);

    const playerUserIdsToAdd = playerUserIds.filter((userId) => !currentPlayerUserIds.has(userId));

    if (banIdsToRemove.length > 0) {
        const deleteResponse = await client
            .from('gm_bans_players')
            .delete()
            .in('id', banIdsToRemove);
        throwIfSupabaseError(deleteResponse);
    }

    if (playerUserIdsToAdd.length === 0) {
        return;
    }

    let gmProfileId = currentBans[0]?.gm?.id;
    if (gmProfileId == null) {
        const gmProfileResponse = await client
            .from('user_profiles')
            .select('id')
            .eq('user_id', gmUserId)
            .single();
        throwIfSupabaseError(gmProfileResponse);

        gmProfileId = gmProfileResponse.data.id;
    }

    const profilesResponse = await client
        .from('user_profiles')
        .select('id, user_id')
        .in('user_id', playerUserIdsToAdd);
    throwIfSupabaseError(profilesResponse);

    const insertResponse = await client
        .from('gm_bans_players')
        .insert(profilesResponse.data.map((profile) => ({
            gm_id: gmProfileId,
            player_id: profile.id,
        })));
    throwIfSupabaseError(insertResponse);
}

async function getPlayersForGame(id: Game['id']): Promise<MinimalUser[]> {
    const response = await client
        .from('games_players')
        .select('player_id, player:user_profiles(user_id, username, image_url, discord_username, role)')
        .eq('game_id', id);
    throwIfSupabaseError(response);

    return response.data.map((dto) => toMinimalUser(dto.player));
}

export async function createGame(dto: CreateGame): Promise<GameRef> {
    const imageUrl = dto.image ? await uploadContentPicture(dto.image) : (dto.imageUrl ?? '');

    // Slim select — the only consumer (`GameForm.vue`) just routes by slug
    // and links a reservation by id. Anything more invites the joins back.
    const response = await client
        .from(table)
        .insert({
            event_id: dto.eventId,
            system_id: dto.systemId,
            game_master_id: dto.gameMasterId,
            max_players: dto.maxPlayers,
            title: dto.title,
            starts_at: dto.startsAt,
            image_url: imageUrl,
            description: dto.description,
            short_description: dto.shortDescription,
            location_id: dto.locationId,
            notice: dto.notice,
            is_draft: dto.isDraft,
            cloned_from_id: dto.clonedFromId,
        })
        .select('id, title')
        .single();
    throwIfSupabaseError(response);

    if (dto.tags.length > 0) {
        const tagsResponse = await client
            .from('games_tags')
            .insert(dto.tags.map((tagId) => ({ game_id: response.data.id, tag_id: tagId })));
        throwIfSupabaseError(tagsResponse);
    }

    if (dto.coGmIds && dto.coGmIds.length > 0) {
        const coGmsResponse = await client
            .from('game_co_gms')
            .insert(dto.coGmIds.map((gmId) => ({ game_id: response.data.id, gm_user_id: gmId })));
        throwIfSupabaseError(coGmsResponse);
    }

    return toGameRef(response.data);
}

// Creates a game from an existing reservation, linking the two atomically.
//
// The slot-cap accounting counts a linked (game, reservation) pair as one
// slot, but the plain `createGame` + `linkGameToReservation` sequence is two
// transactions: on the game INSERT the reservation is still unlinked, so a
// full event double-counts and the cap trigger rejects the insert. The
// `create_game_from_reservation` RPC does the insert + link (+ publish) in one
// transaction, ordered so the game is only ever counted once linked. Tags and
// co-GMs stay as separate follow-up inserts here — they don't touch the cap.
export async function createGameFromReservation(
    dto: CreateGame,
    reservationId: GameReservation['id'],
): Promise<GameRef> {
    const imageUrl = dto.image ? await uploadContentPicture(dto.image) : (dto.imageUrl ?? '');

    const response = await client.rpc('create_game_from_reservation', {
        p_reservation_id: reservationId,
        p_event_id: dto.eventId,
        p_system_id: dto.systemId,
        p_game_master_id: dto.gameMasterId,
        p_max_players: dto.maxPlayers,
        p_title: dto.title,
        p_starts_at: dto.startsAt,
        p_image_url: imageUrl,
        p_description: dto.description,
        p_short_description: dto.shortDescription,
        p_location_id: dto.locationId,
        p_notice: dto.notice,
        p_is_draft: dto.isDraft ?? false,
    });
    throwIfSupabaseError(response);

    const gameId = response.data;

    if (dto.tags.length > 0) {
        const tagsResponse = await client
            .from('games_tags')
            .insert(dto.tags.map((tagId) => ({ game_id: gameId, tag_id: tagId })));
        throwIfSupabaseError(tagsResponse);
    }

    if (dto.coGmIds && dto.coGmIds.length > 0) {
        const coGmsResponse = await client
            .from('game_co_gms')
            .insert(dto.coGmIds.map((gmId) => ({ game_id: gameId, gm_user_id: gmId })));
        throwIfSupabaseError(coGmsResponse);
    }

    return toGameRef({ id: gameId, title: dto.title });
}

// Returns the newly inserted player so callers can patch local state without
// refetching the entire game.
export async function addPlayerToGame(gameId: Game['id'], playerId: MinimalUser['id']): Promise<MinimalUser> {
    const response = await client
        .from('games_players')
        .insert({ game_id: gameId, player_id: playerId })
        .select('player:user_profiles!games_players_player_id_fkey(user_id, username, image_url, discord_username, role)')
        .single();
    throwIfSupabaseError(response);

    return toMinimalUser(response.data.player);
}

export async function removePlayerFromGame(gameId: Game['id'], playerId: MinimalUser['id']): Promise<void> {
    const response = await client
        .from('games_players')
        .delete()
        .eq('game_id', gameId)
        .eq('player_id', playerId);
    throwIfSupabaseError(response);
}

export async function updateGame(dto: UpdateGame): Promise<GameRef> {
    const updateDto: Partial<GameDto> = {
        event_id: dto.eventId,
        system_id: dto.systemId,
        game_master_id: dto.gameMasterId,
        max_players: dto.maxPlayers,
        title: dto.title,
        starts_at: dto.startsAt,
        description: dto.description,
        short_description: dto.shortDescription,
        location_id: dto.locationId,
        notice: dto.notice,
        is_draft: dto.isDraft,
    };

    if (dto.image) {
        updateDto.image_url = await uploadContentPicture(dto.image);
    }

    const response = await client
        .from(table)
        .update(updateDto)
        .eq('id', dto.id)
        .select('id, title')
        .single();
    throwIfSupabaseError(response);

    // Drop old tags
    const deleteTagsResponse = await client
        .from('games_tags')
        .delete()
        .eq('game_id', dto.id);
    throwIfSupabaseError(deleteTagsResponse);

    if (dto.tags.length > 0) {
        const insertTagsResponse = await client
            .from('games_tags')
            .insert(dto.tags.map((tagId) => ({ game_id: dto.id, tag_id: tagId })));
        throwIfSupabaseError(insertTagsResponse);
    }

    // Co-GMs follow the same delete-then-insert pattern as tags. `coGmIds`
    // being undefined means the caller doesn't manage co-GMs on this update
    // (e.g. a non-admin path); leave existing rows alone. An empty array
    // means "clear all co-GMs" (matches what GameForm sends when the primary
    // GM switches from Bot to non-Bot).
    if (dto.coGmIds !== undefined) {
        const deleteCoGmsResponse = await client
            .from('game_co_gms')
            .delete()
            .eq('game_id', dto.id);
        throwIfSupabaseError(deleteCoGmsResponse);

        if (dto.coGmIds.length > 0) {
            const insertCoGmsResponse = await client
                .from('game_co_gms')
                .insert(dto.coGmIds.map((gmId) => ({ game_id: dto.id, gm_user_id: gmId })));
            throwIfSupabaseError(insertCoGmsResponse);
        }
    }

    return toGameRef(response.data);
}

function toGameRef(row: { id: string; title: string }): GameRef {
    return {
        id: row.id,
        slug: `${slugify(row.title)}--${row.id}`,
    };
}

export async function publishGameDraft(gameId: Game['id']): Promise<void> {
    const response = await client
        .from(table)
        .update({ is_draft: false })
        .eq('id', gameId);
    throwIfSupabaseError(response);
}

export async function approveGame(gameId: Game['id']): Promise<void> {
    const response = await client
        .from(table)
        .update({ approved_at: new Date().toISOString() })
        .eq('id', gameId);
    throwIfSupabaseError(response);
}

export async function unapproveGame(gameId: Game['id']): Promise<void> {
    const response = await client
        .from(table)
        .update({ approved_at: null })
        .eq('id', gameId);
    throwIfSupabaseError(response);
}

export async function makeGameDraft(gameId: Game['id']): Promise<void> {
    const response = await client
        .from(table)
        .update({ is_draft: true })
        .eq('id', gameId);
    throwIfSupabaseError(response);
}

// Atomically-ish cancel a game: bulk-delete all `games_players` for the
// game and flip the game row back to an unapproved draft. Collapses what
// used to be N parallel single-player deletes + two separate updates into
// two round-trips total. Callers should patch local state from the
// known-good post-cancel shape rather than refetching.
export async function cancelGame(gameId: Game['id']): Promise<void> {
    const deleteResponse = await client
        .from('games_players')
        .delete()
        .eq('game_id', gameId);
    throwIfSupabaseError(deleteResponse);

    const updateResponse = await client
        .from(table)
        .update({ is_draft: true, approved_at: null })
        .eq('id', gameId);
    throwIfSupabaseError(updateResponse);
}

export async function findGamesForPlayer(userId: User['id']) {
    const [participated, gameMastered] = await Promise.all([
        findGamesWhereIsPlayer(userId),
        findGamesWhereIsGameMaster(userId),
    ]);

    const participatedInThePast = participated.filter((game) => isBefore(game.startsAt, new Date()));
    const participatedInTheFuture = participated.filter((game) => !isBefore(game.startsAt, new Date()));

    return {
        played: participatedInThePast.sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime()),
        applied: participatedInTheFuture.sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime()),
        gmed: gameMastered.sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime()),
    };
}

async function findGamesWhereIsPlayer(userId: User['id']): Promise<MinimalGame[]> {
    const response = await client
        .from('games_players')
        .select(`
            game_id,
            games:game_id(
                id,
                legacy_id,
                events(
                    id,
                    name,
                    description,
                    opens_at,
                    dates
                ),
                game_master:game_master_id(
                    user_id
                ),
                systems(
                    id,
                    name,
                    description
                ),
                title,
                starts_at,
                image_url,
                short_description,
                approved_at,
                is_draft
            )`)
        .eq('player_id', userId)
        .neq('games.is_draft', true)
        .not('games.approved_at', 'is', 'null');
    throwIfSupabaseError(response);

    return response.data.map((dto) => parseMinimalGame(dto.games));
}

async function findGamesWhereIsGameMaster(userId: User['id']): Promise<MinimalGame[]> {

    const response = await client
        .from('games')
        .select(`
            id,
            legacy_id,
            game_master_id,
            events(
                id,
                name,
                description,
                opens_at,
                dates
            ),
            game_master:game_master_id(
                user_id
            ),
            systems(
                id,
                name,
                description
            ),
            title,
            starts_at,
            image_url,
            short_description,
            approved_at,
            is_draft,
            tags(
                id,
                name
            )`
        )
        .eq('game_master_id', userId)
        .neq('is_draft', true)
        .not('approved_at', 'is', 'null');
    throwIfSupabaseError(response);

    return response.data.map((dto) => parseMinimalGame(dto));
}

export async function getMyEditableGames(userId: User['id']): Promise<Game[]> {
    const response = await client
        .from(table)
        .select(gameListSelectExpression)
        .eq('game_master_id', userId)
        .is('approved_at', null)
        .neq('is_draft', true);
    throwIfSupabaseError(response);

    return response.data.map(parseListGame).sort((a, b) => b.startsAt.getTime() - a.startsAt.getTime());
}

export async function deleteGame(id: Game['id']): Promise<void> {
    const response = await client
        .from(table)
        .delete()
        .eq('id', id);
    throwIfSupabaseError(response);
}

export async function getGamesForSearch(): Promise<SearchableGame[]> {
    const response = await client
        .from(table)
        .select(`
            id,
            title,
            system_id,
            systems(
                name
            ),
            game_master_id,
            game_master:game_master_id(
                username
            ),
            event_id,
            events(
                name
            ),
            starts_at
        `)
        .neq('is_draft', true)
        .not('approved_at', 'is', 'null');
    throwIfSupabaseError(response);

    return response.data.map((dto) => ({
        id: dto.id,
        title: dto.title,
        systemId: dto.system_id,
        systemName: dto.systems.name,
        gameMasterId: dto.game_master_id,
        gameMasterName: dto.game_master.username ?? '',
        eventId: dto.event_id,
        eventName: dto.events.name,
        date: new Date(dto.starts_at),
    }));
}

function parseMinimalGame(dto: MinimalGameDb): MinimalGame {
    return {
        id: dto.id,
        legacyId: dto.legacy_id ?? undefined,
        slug: slugify(dto.title) + `--${dto.id}`,
        event: {
            id: dto.events.id,
            slug: slugify(dto.events.name) + `--${dto.events.id}`,
            name: dto.events.name,
            description: dto.events.description,
            opensAt: new Date(dto.events.opens_at),
            dates: (dto.events.dates as string[]).map((date: string) => new Date(date)),
        },
        gameMaster: {
            id: dto.game_master.user_id,
        },
        system: {
            id: dto.systems.id,
            slug: slugify(dto.systems.name) + `--${dto.systems.id}`,
            name: dto.systems.name,
            description: dto.systems.description ?? undefined,
        },
        title: dto.title,
        startsAt: new Date(dto.starts_at),
        imageUrl: dto.image_url,
        shortDescription: dto.short_description,
        approvedAt: dto.approved_at ? new Date(dto.approved_at) : undefined,
        isDraft: dto.is_draft ?? undefined,
        tags: [
            ...(dto.tags ?? []).map((tag) => ({
                id: tag.id,
                name: tag.name,
                slug: slugify(tag.name) + `--${tag.id}`,
            })),
        ],
    };
}

// Builds a `Game` from the lean list projection. `description`, `notice`, and
// `location` aren't fetched on this path — list consumers don't read them, and
// inflating empty strings keeps the shared `Game` shape so cards/route
// components don't need a separate union.
function parseListGame(dto: GameListDb): Game {
    const playerCount = dto.players?.[0]?.count ?? 0;
    return {
        id: dto.id,
        legacyId: dto.legacy_id ?? undefined,
        slug: slugify(dto.title) + `--${dto.id}`,
        event: {
            id: dto.events.id,
            slug: slugify(dto.events.name) + `--${dto.events.id}`,
            name: dto.events.name,
            description: '',
            opensAt: new Date(dto.events.opens_at),
            dates: [],
        },
        system: {
            id: dto.systems.id,
            slug: slugify(dto.systems.name) + `--${dto.systems.id}`,
            name: dto.systems.name,
        },
        gameMaster: toMinimalUser(dto.game_master),
        players: [],
        bannedPlayers: [],
        playerCount,
        maxPlayers: dto.max_players,
        title: dto.title,
        startsAt: new Date(dto.starts_at),
        opensAt: new Date(dto.events.opens_at),
        imageUrl: dto.image_url,
        description: '',
        shortDescription: dto.short_description,
        location: undefined,
        notice: undefined,
        approvedAt: dto.approved_at ? new Date(dto.approved_at) : undefined,
        isDraft: dto.is_draft ?? undefined,
        tags: dto.tags.map((tag) => ({
            id: tag.id,
            name: tag.name,
            slug: slugify(tag.name) + `--${tag.id}`,
        })),
    };
}

function parseGame(dto: GameDb): Game {
    const { gm_bans, ...gameMasterProfile } = dto.game_master;
    const bannedPlayers = (gm_bans ?? []).flatMap(
        (ban) => ban.player == null ? [] : [toMinimalUser(ban.player)],
    );

    const coGms = (dto.co_gms ?? []).flatMap(
        (row) => row.gm == null ? [] : [toMinimalUser(row.gm)],
    );

    return {
        id: dto.id,
        legacyId: dto.legacy_id ?? undefined,
        slug: slugify(dto.title) + `--${dto.id}`,
        event: {
            id: dto.events.id,
            slug: slugify(dto.events.name) + `--${dto.events.id}`,
            name: dto.events.name,
            description: dto.events.description,
            opensAt: new Date(dto.events.opens_at),
            dates: (dto.events.dates as string[]).map((date: string) => new Date(date)),
            maximumGames: dto.events.max_games ?? undefined,
        },
        system: {
            id: dto.systems.id,
            slug: slugify(dto.systems.name) + `--${dto.systems.id}`,
            name: dto.systems.name,
            description: dto.systems.description ?? undefined,
        },
        gameMaster: toMinimalUser(gameMasterProfile),
        players: [],
        bannedPlayers,
        maxPlayers: dto.max_players,
        title: dto.title,
        startsAt: new Date(dto.starts_at),
        opensAt: new Date(dto.events.opens_at),
        imageUrl: dto.image_url,
        description: dto.description,
        shortDescription: dto.short_description,
        location: {
            id: dto.locations.id,
            name: dto.locations.name,
            address: dto.locations.address ?? undefined,
        },
        notice: dto.notice ?? undefined,
        approvedAt: dto.approved_at ? new Date(dto.approved_at) : undefined,
        isDraft: dto.is_draft ?? undefined,
        clonedFromId: dto.cloned_from_id ?? undefined,
        coGms,
        tags: [
            ...dto.tags.map((tag) => ({
                id: tag.id,
                name: tag.name,
                slug: slugify(tag.name) + `--${tag.id}`,
            }))
        ],
    };
}

type GameDb = {
    id: string;
    legacy_id: string | null;
    events: {
        id: string;
        name: string;
        description: string;
        opens_at: string;
        dates: Json;
        max_games: number | null;
    };
    systems: {
        id: string;
        name: string;
        description: string | null;
    };
    game_master: {
        user_id: string;
        username: string | null;
        image_url: string | null;
        discord_username: string | null;
        role: MinimalUser['role'];
        gm_bans: Array<{
            player: {
                user_id: string;
                username: string | null;
                image_url: string | null;
                discord_username: string | null;
                role: MinimalUser['role'];
            } | null;
        }> | null;
    };
    max_players: number;
    title: string;
    starts_at: string;
    image_url: string;
    description: string;
    short_description: string;
    locations: {
        id: string;
        name: string;
        address: string | null;
    };
    notice: string | null;
    approved_at: string | null;
    is_draft: boolean | null;
    cloned_from_id: string | null;
    tags: {
        id: string;
        name: string;
    }[];
    co_gms: Array<{
        gm: {
            user_id: string;
            username: string | null;
            image_url: string | null;
            discord_username: string | null;
            role: MinimalUser['role'];
        } | null;
    }> | null;
};

type GameListDb = {
    id: string;
    legacy_id: string | null;
    events: {
        id: string;
        name: string;
        opens_at: string;
    };
    systems: {
        id: string;
        name: string;
    };
    game_master: {
        user_id: string;
        username: string | null;
        image_url: string | null;
        discord_username: string | null;
        role: MinimalUser['role'];
    };
    max_players: number;
    title: string;
    starts_at: string;
    image_url: string;
    short_description: string;
    approved_at: string | null;
    is_draft: boolean | null;
    tags: {
        id: string;
        name: string;
    }[];
    players: { count: number }[] | null;
};

type MinimalGameDb = {
    id: string;
    legacy_id: string | null;
    events: {
        id: string;
        name: string;
        description: string;
        opens_at: string;
        dates: Json;
    };
    game_master: {
        user_id: string;
    };
    systems: {
        id: string;
        name: string;
        description: string | null;
    };
    title: string;
    starts_at: string;
    image_url: string;
    short_description: string;
    approved_at: string | null;
    is_draft: boolean | null;
    tags?: {
        id: string;
        name: string;
    }[];
};

type GameDto = {
    id: string;
    event_id: Event['id'];
    system_id: System['id'];
    game_master_id: MinimalUser['id'];
    max_players: number;
    title: string;
    starts_at: string;
    image_url: string;
    description: string;
    short_description: string;
    location_id: Location['id'];
    notice?: string;
    approved_at?: string;
    is_draft?: boolean;
};
